import React, { useState, useEffect, useRef } from 'react';
import axiosClient from '../api/axiosClient';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Fab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Stack,
  TextField,
  MenuItem,
  InputLabel,
  FormControl,
  Select,
  Dialog,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Autocomplete } from '@mui/material';
import { listUsers } from '../api/users';
import type { UserDto } from '../api/users';
import {
  Upload,
  Description,
  Assessment,
  Schedule,
  CloudUpload,
  ExpandMore,
  ExpandLess,
  Link,
  Person,
  Email,
  Phone,
  Settings,
  Send,
  CheckCircle,
} from '@mui/icons-material';

type DashboardRecord = {
  id: number;
  fileName?: string;
  totalRecords?: number;
  successCount?: number;
  errorCount?: number;
  processedAt?: string;
  mappingConfigId?: number;
  mappingConfig?: {
    id: number;
    name: string;
    description?: string;
  };
  mappingConfigs?: Array<{
    id: number;
    name: string;
    description?: string;
    reportType?: string | null;
    reportTemplate?: string | null;
    fieldsCount?: number;
  }>;
  user?: {
    id: number;
    username: string;
    email: string;
    role: string;
  };
};

type NormalizedClient = {
  grantorName?: string;
  email?: string;
  phone?: string;
  authorizationTo?: string;
  campaign?: string;
  [key: string]: unknown;
};

type RecordDetails = {
  mappingConfig?: {
    id: number;
    name: string;
    description?: string;
  };
  mappingConfigs?: Array<{
    id: number;
    name: string;
    description?: string;
    reportType?: string | null;
    reportTemplate?: string | null;
    fieldsCount?: number;
  }>;
  summary?: { total?: number; success?: number; errors?: number };
  results?: Array<{
    clientData?: Record<string, any>;
    normalized?: NormalizedClient;
    nif?: string;
    status?: string;
    message?: string;
    shareLink?: string;
    sentEmailAt?: string | null;
  }>;
};

const DashboardPage: React.FC = () => {
  const [data, setData] = useState<{stats: Record<string, number>, records: DashboardRecord[]} | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedRecord, setExpandedRecord] = useState<number | null>(null);
  const [recordDetails, setRecordDetails] = useState<RecordDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();

  // Estado de filtros para exportación CSV (deben declararse antes de cualquier return condicional)
  const [filterStart, setFilterStart] = React.useState<string>('');
  const [filterEnd, setFilterEnd] = React.useState<string>('');
  const [filterStatus, setFilterStatus] = React.useState<'todos' | 'correcto' | 'incorrecto'>('todos');
  const [filterUserId, setFilterUserId] = React.useState<string>('');
  const [adminUsers, setAdminUsers] = React.useState<UserDto[]>([]);
  const [selectedUser, setSelectedUser] = React.useState<UserDto | null>(null);
  const [campaigns, setCampaigns] = React.useState<string[]>([]);
  const [filterCampaign, setFilterCampaign] = React.useState<string>('');

  // Modal de documentos generados
  const [docsOpen, setDocsOpen] = useState(false);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsList, setDocsList] = useState<Array<{ 
    id: number; 
    nif?: string | null; 
    nameDocumentDocx?: string | null; 
    nameDocumentPdf?: string | null; 
    docType?: string | null; 
    templateName?: string | null; 
    indexInBatch?: number | null;
    tipo?: 'Generated' | 'Uploaded';
    originalFileName?: string | null;
    fileType?: string | null;
    fileSize?: number | null;
    nextcloudPath?: string | null;
    nextcloudUrl?: string | null;
  }>>([]);
  const [docsClientNif, setDocsClientNif] = useState<string>('');
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [previewName, setPreviewName] = useState<string>('');

  // Estado para manejar cambios temporales de sentEmailAt antes de aplicar
  const [pendingSentEmailUpdates, setPendingSentEmailUpdates] = useState<Record<string, { checked: boolean; timestamp: string | null }>>({});
  const [updatingSentEmail, setUpdatingSentEmail] = useState<Record<string, boolean>>({});

  // Filtros por campos normalizados
  type NormalizedFieldKey = 'nif' | 'email' | 'phone' | 'campaign' | 'grantorName' | 'contactEmail' | 'authorizationTo' | 'responsibleFrom';
  const normalizedFieldKeys: NormalizedFieldKey[] = ['nif', 'email', 'phone', 'campaign', 'grantorName', 'contactEmail', 'authorizationTo', 'responsibleFrom'];
  const [normalizedOptions, setNormalizedOptions] = useState<Record<NormalizedFieldKey, string[]>>({
    nif: [],
    email: [],
    phone: [],
    campaign: [],
    grantorName: [],
    contactEmail: [],
    authorizationTo: [],
    responsibleFrom: [],
  });
  const [normalizedSelected, setNormalizedSelected] = useState<Record<NormalizedFieldKey, string[]>>({
    nif: [],
    email: [],
    phone: [],
    campaign: [],
    grantorName: [],
    contactEmail: [],
    authorizationTo: [],
    responsibleFrom: [],
  });

  // Referencia para rastrear el último registro expandido y solo resetear filtros cuando cambia
  const lastExpandedRecordRef = useRef<number | null>(null);

  // Recalcular opciones cuando cambia el registro expandido (no cuando solo se actualiza recordDetails)
  useEffect(() => {
    // Solo resetear filtros cuando se abre un registro diferente (no cuando se cierra o se actualiza el mismo)
    if (expandedRecord !== null && expandedRecord !== lastExpandedRecordRef.current) {
      setNormalizedSelected({ nif: [], email: [], phone: [], campaign: [], grantorName: [], contactEmail: [], authorizationTo: [], responsibleFrom: [] });
      lastExpandedRecordRef.current = expandedRecord;
    } else if (expandedRecord === null) {
      lastExpandedRecordRef.current = null;
    }
  }, [expandedRecord]);

  // Recalcular opciones disponibles cuando cambia recordDetails (sin resetear filtros)
  useEffect(() => {
    if (!recordDetails || !Array.isArray(recordDetails.results)) {
      setNormalizedOptions({ nif: [], email: [], phone: [], campaign: [], grantorName: [], contactEmail: [], authorizationTo: [], responsibleFrom: [] });
      return;
    }
    const sets: Record<NormalizedFieldKey, Set<string>> = {
      nif: new Set(),
      email: new Set(),
      phone: new Set(),
      campaign: new Set(),
      grantorName: new Set(),
      contactEmail: new Set(),
      authorizationTo: new Set(),
      responsibleFrom: new Set(),
    };
    for (const r of recordDetails.results) {
      const n = (r as any).normalized || {};
      (normalizedFieldKeys as NormalizedFieldKey[]).forEach((k) => {
        const v = (n?.[k] ?? '') as string;
        const value = typeof v === 'string' ? v.trim() : '';
        if (value) sets[k].add(value);
      });
    }
    setNormalizedOptions({
      nif: Array.from(sets.nif).sort(),
      email: Array.from(sets.email).sort(),
      phone: Array.from(sets.phone).sort(),
      campaign: Array.from(sets.campaign).sort(),
      grantorName: Array.from(sets.grantorName).sort(),
      contactEmail: Array.from(sets.contactEmail).sort(),
      authorizationTo: Array.from(sets.authorizationTo).sort(),
      responsibleFrom: Array.from(sets.responsibleFrom).sort(),
    });
  }, [recordDetails]);

  const applyNormalizedFilters = (results: any[] | undefined) => {
    if (!Array.isArray(results)) return [];
    const activeKeys = normalizedFieldKeys.filter((k) => (normalizedSelected[k] || []).length > 0);
    if (activeKeys.length === 0) return results;
    return results.filter((r) => {
      const n = (r as any).normalized || {};
      for (const k of activeKeys) {
        const val = ((n?.[k] ?? '') as string).trim();
        if (!normalizedSelected[k].includes(val)) return false;
      }
      return true;
    });
  };

  // Cargar usuarios para admin (para el autocomplete)
  React.useEffect(() => {
    const loadUsers = async () => {
      if (user?.role !== 'admin') return;
      try {
        const res = await listUsers({ page: 1, limit: 100 });
        setAdminUsers(res.data || []);
      } catch (e) {
        // silencioso
      }
    };
    loadUsers();
  }, [user?.role]);

  // Cargar campañas distintas para filtro
  React.useEffect(() => {
    const loadCampaigns = async () => {
      try {
        const { data } = await axiosClient.get('/dashboard/campaigns');
        setCampaigns(Array.isArray(data?.campaigns) ? data.campaigns : []);
      } catch {}
    };
    loadCampaigns();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axiosClient.get('/dashboard/data'); 
        setData(response.data);
      } catch (err) {
        console.error('Error al cargar datos del dashboard:', err);
        setError(t('dashboard.loadError', 'Error al cargar los datos del dashboard. Verifique que el servicio esté funcionando.'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleRecordClick = async (recordId: number) => {
    if (expandedRecord === recordId) {
      setExpandedRecord(null);
      setRecordDetails(null);
      setPendingSentEmailUpdates({});
      return;
    }

    setLoadingDetails(true);
    try {
      const response = await axiosClient.get(`/dashboard/record/${recordId}`);
      setRecordDetails(response.data as RecordDetails);
      setExpandedRecord(recordId);
      // Resetear cambios pendientes al cargar nuevos detalles
      setPendingSentEmailUpdates({});
    } catch (err) {
      console.error('Error al cargar detalles del registro:', err);
      setError('Error al cargar los detalles del registro');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSentEmailCheckboxChange = (nif: string, checked: boolean) => {
    const timestamp = checked ? new Date().toISOString() : null;
    setPendingSentEmailUpdates(prev => ({
      ...prev,
      [nif]: { checked, timestamp },
    }));
  };

  const handleApplySentEmail = async (nif: string) => {
    const update = pendingSentEmailUpdates[nif];
    if (!update) return;

    setUpdatingSentEmail(prev => ({ ...prev, [nif]: true }));
    try {
      const response = await axiosClient.put(`/dashboard/client/${encodeURIComponent(nif)}/sent-email`, {
        sentEmailAt: update.timestamp,
      });

      if (response.data.success) {
        // Actualizar el estado local del recordDetails
        if (recordDetails && recordDetails.results) {
          setRecordDetails({
            ...recordDetails,
            results: recordDetails.results.map(r => 
              r.nif === nif 
                ? { ...r, sentEmailAt: update.timestamp }
                : r
            ),
          });
        }
        // Eliminar el cambio pendiente
        setPendingSentEmailUpdates(prev => {
          const newPending = { ...prev };
          delete newPending[nif];
          return newPending;
        });
      } else {
        setError(response.data.message || 'Error al actualizar sentEmailAt');
      }
    } catch (err: any) {
      console.error('Error al actualizar sentEmailAt:', err);
      setError(err.response?.data?.message || 'Error al actualizar sentEmailAt');
    } finally {
      setUpdatingSentEmail(prev => ({ ...prev, [nif]: false }));
    }
  };

  const openDocumentsModal = async (processedId: number, clientNif?: string) => {
    setDocsOpen(true);
    setDocsLoading(true);
    setDocsClientNif(clientNif || '');
    try {
      const { data } = await axiosClient.get('/upload/documents', {
        params: { processedDataId: String(processedId), nif: clientNif || '' },
      });
      setDocsList(Array.isArray(data?.data) ? data.data : []);
    } catch (e) {
      console.error('Error al listar documentos:', e);
      setDocsList([]);
    } finally {
      setDocsLoading(false);
    }
  };

  const closeDocumentsModal = () => {
    setDocsOpen(false);
    setDocsList([]);
    if (previewUrl) {
      try { URL.revokeObjectURL(previewUrl); } catch {}
    }
    setPreviewUrl('');
    setPreviewName('');
  };

  const downloadDocument = async (id: number, kind: 'pdf' | 'docx', suggestedName?: string | null) => {
    try {
      const response = await axiosClient.get(`/upload/documents/${id}/${kind}`, {
        params: { download: '1' },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: response.headers['content-type'] || (kind === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = suggestedName || `documento.${kind}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error al descargar documento:', e);
      alert('No se pudo descargar el documento.');
    }
  };

  const downloadUploadedFile = async (id: number, fileName?: string | null) => {
    try {
      const response = await axiosClient.get(`/manage-files/files/${id}`, {
        params: { download: '1' },
        responseType: 'blob',
      });
      const contentType = response.headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // Extraer el nombre del archivo del header Content-Disposition si está disponible
      const contentDisposition = response.headers['content-disposition'];
      let downloadName = fileName || `archivo_${id}`;
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (fileNameMatch && fileNameMatch[1]) {
          downloadName = fileNameMatch[1];
        }
      }
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error al descargar archivo subido:', e);
      alert('No se pudo descargar el archivo.');
    }
  };

  const previewUploadedFile = async (id: number, fileName?: string | null) => {
    try {
      const response = await axiosClient.get(`/manage-files/files/${id}`, {
        responseType: 'blob',
      });
      const contentType = response.headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      
      // Solo mostrar preview para PDFs
      if (contentType.includes('pdf')) {
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
        setPreviewName(fileName || `archivo_${id}`);
      } else {
        // Para otros tipos de archivo, descargar directamente
        downloadUploadedFile(id, fileName);
      }
    } catch (e) {
      console.error('Error al ver archivo subido:', e);
      alert('No se pudo ver el archivo.');
    }
  };

  const previewPdf = async (id: number, name?: string | null) => {
    try {
      const response = await axiosClient.get(`/upload/documents/${id}/pdf`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      if (previewUrl) {
        try { URL.revokeObjectURL(previewUrl); } catch {}
      }
      setPreviewUrl(url);
      setPreviewName(name || 'documento.pdf');
    } catch (e) {
      console.error('Error al previsualizar PDF:', e);
      alert('No se pudo cargar la previsualización del PDF.');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          {t('dashboard.loading', 'Cargando datos del Dashboard...')}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  const totalFiles = data?.stats?.totalFiles || 0;
  const totalRecords = data?.stats?.totalRecords || 0;
  const successfulFiles = data?.stats?.successfulFiles || 0;
  const errorFiles = data?.stats?.errorFiles || 0;


  const handleDownloadCsv = async () => {
    try {
      const params: Record<string, string> = {};
      if (filterStart) params.startDate = filterStart;
      if (filterEnd) params.endDate = filterEnd;
      if (filterStatus && filterStatus !== 'todos') params.status = filterStatus;
    if (user?.role === 'admin' && filterUserId) params.userId = filterUserId;
    if (filterCampaign) params.campaign = filterCampaign;

      // Usar axios autenticado para incluir el header Authorization (interceptor)
      const response = await axiosClient.get('/dashboard/export', {
        params,
        responseType: 'blob',
      });

      // Intentar obtener filename del header Content-Disposition
      const disposition = response.headers['content-disposition'] as string | undefined;
      let filename = 'export.csv';
      if (disposition) {
        const match = /filename\*?=([^;]+)/i.exec(disposition);
        if (match) {
          const raw = match[1].trim().replace(/^UTF-8''/, '').replace(/"/g, '');
          try { filename = decodeURIComponent(raw); } catch { filename = raw; }
        }
      }

      // Crear URL de blob y forzar descarga
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8;' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error('Error al descargar CSV:', e);
      alert('No se pudo descargar el CSV. Verifique que la sesión esté activa y los filtros sean válidos.');
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 4 }}>
        {t('dashboard.title', 'Dashboard')}
      </Typography>

      {/* Estadísticas principales */}
      <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
        <Card sx={{ flex: '1 1 300px', minWidth: '250px' }}>
          <CardContent>
            <Box display="flex" alignItems="center">
              <Description color="primary" sx={{ mr: 2, fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  {t('dashboard.processedFiles', 'Archivos Procesados')}
                </Typography>
                <Typography variant="h4">
                  {totalFiles}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        
        <Card sx={{ flex: '1 1 300px', minWidth: '250px' }}>
          <CardContent>
            <Box display="flex" alignItems="center">
              <Assessment color="secondary" sx={{ mr: 2, fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  {t('dashboard.totalRecords', 'Total Registros')}
                </Typography>
                <Typography variant="h4">
                  {totalRecords.toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 300px', minWidth: '250px' }}>
          <CardContent>
            <Box display="flex" alignItems="center">
              <Assessment color="success" sx={{ mr: 2, fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  {t('dashboard.successfulFiles', 'Archivos Exitosos')}
                </Typography>
                <Typography variant="h4">
                  {successfulFiles}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 300px', minWidth: '250px' }}>
          <CardContent>
            <Box display="flex" alignItems="center">
              <Assessment color="error" sx={{ mr: 2, fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  {t('dashboard.errorFiles', 'Archivos con Errores')}
                </Typography>
                <Typography variant="h4">
                  {errorFiles}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Filtros de exportación CSV (oculto: export antiguo) */}
      <Card sx={{ mb: 3, display: 'none' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('dashboard.exportCsv', 'Exportar CSV')}
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-end' }}>
            <TextField
              label={t('common.from', 'Desde')}
              type="date"
              value={filterStart}
              onChange={(e) => setFilterStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label={t('common.to', 'Hasta')}
              type="date"
              value={filterEnd}
              onChange={(e) => setFilterEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel id="status-label">{t('common.status', 'Estado')}</InputLabel>
              <Select
                labelId="status-label"
                label={t('common.status', 'Estado')}
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as any)}
              >
                <MenuItem value="todos">{t('common.all', 'Todos')}</MenuItem>
                <MenuItem value="correcto">{t('common.correct', 'Correcto')}</MenuItem>
                <MenuItem value="incorrecto">{t('common.incorrect', 'Incorrecto')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 220 }}>
              <InputLabel id="campaign-label">{t('dashboard.campaign', 'Campaña')}</InputLabel>
              <Select
                labelId="campaign-label"
                label={t('dashboard.campaign', 'Campaña')}
                value={filterCampaign}
                onChange={(e) => setFilterCampaign(e.target.value)}
              >
                <MenuItem value="">{t('common.allParen', '(Todas)')}</MenuItem>
                {campaigns.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {user?.role === 'admin' && (
              <Autocomplete
                options={adminUsers || []}
                getOptionLabel={(option) => option?.username ?? ''}
                isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                value={selectedUser}
                onChange={(_, val) => {
                  setSelectedUser(val);
                  setFilterUserId(val ? String(val.id) : '');
                }}
                renderInput={(params) => (
                  <TextField {...params} label={t('dashboard.userUsername', 'Usuario (username)')} placeholder={t('dashboard.selectUser', 'Selecciona usuario')} />
                )}
                sx={{ minWidth: 260 }}
              />
            )}
            <Button variant="contained" onClick={handleDownloadCsv}>
              {t('dashboard.downloadCsv', 'Descargar CSV')}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Lista de archivos procesados */}
      <Card>
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom>
            {t('dashboard.processedFiles', 'Archivos Procesados')}
          </Typography>
          
          {data && data.records && data.records.length > 0 ? (
            // Vista de tabla para administradores
            user?.role === 'admin' ? (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('common.file', 'Archivo')}</TableCell>
                      <TableCell>{t('common.user', 'Usuario')}</TableCell>
                      <TableCell>{t('common.records', 'Registros')}</TableCell>
                      <TableCell>{t('common.status', 'Estado')}</TableCell>
                      <TableCell>{t('common.date', 'Fecha')}</TableCell>
                      <TableCell>{t('common.actions', 'Acciones')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.records.map((item: DashboardRecord) => (
                      <TableRow 
                        key={item.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleRecordClick(item.id)}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Description color="primary" sx={{ mr: 1 }} />
                            <Typography variant="body2" fontWeight="medium">
                              {item.fileName || t('common.unknownFile', 'Archivo desconocido')}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: 'primary.main' }}>
                              {item.user?.username?.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {item.user?.username || t('common.unknownUser', 'Usuario desconocido')}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {item.user?.email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={`${item.totalRecords ?? 0} ${t('common.records', 'registros')}`} 
                            size="small" 
                            color="primary" 
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" flexDirection="column" gap={0.5}>
                            {(item.successCount ?? 0) > 0 && (
                              <Chip 
                                label={`${item.successCount} ${t('common.successful', 'exitosos')}`} 
                                size="small" 
                                color="success" 
                              />
                            )}
                            {(item.errorCount ?? 0) > 0 && (
                              <Chip 
                                label={`${item.errorCount} ${t('common.errors', 'errores')}`} 
                                size="small" 
                                color="error" 
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            icon={<Schedule />}
                            label={new Date(item.processedAt || '').toLocaleDateString(i18n.language === 'es' ? 'es-ES' : i18n.language,
                              {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                            size="small" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {loadingDetails && expandedRecord === item.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            expandedRecord === item.id ? <ExpandLess /> : <ExpandMore />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              // Vista de lista para usuarios normales
              <List>
                {data?.records?.map((item: DashboardRecord, index: number) => {
                  const isExpanded = expandedRecord === item.id;
                  
                  return (
                    <React.Fragment key={index}>
                      <ListItem 
                        component="div"
                        onClick={() => handleRecordClick(item.id)}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                      >
                        <ListItemIcon>
                          <Description color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={item.fileName || t('common.unknownFile', 'Archivo desconocido')}
                          secondary={
                            <Typography component="div" variant="body2" color="textSecondary">
                              <Box component="div">
                              <Chip 
                                  label={`${item.totalRecords ?? 0} ${t('common.records', 'registros')}`} 
                                size="small" 
                                color="primary" 
                                sx={{ mr: 1 }}
                              />
                                {(item.successCount ?? 0) > 0 && (
                                <Chip 
                                    label={`${item.successCount ?? 0} ${t('common.successful', 'exitosos')}`} 
                                  size="small" 
                                  color="success" 
                                  sx={{ mr: 1 }}
                                />
                              )}
                                {(item.errorCount ?? 0) > 0 && (
                                <Chip 
                                    label={`${item.errorCount ?? 0} ${t('common.errors', 'errores')}`} 
                                  size="small" 
                                  color="error" 
                                  sx={{ mr: 1 }}
                                />
                              )}
                              {item.mappingConfigs && item.mappingConfigs.length > 0 ? (
                                item.mappingConfigs.length === 1 ? (
                                  <Chip 
                                    icon={<Settings />}
                                    label={`${t('dashboard.mapping', 'Mapeo')}: ${item.mappingConfigs[0].name}`}
                                    size="small" 
                                    color="info" 
                                    variant="outlined"
                                    sx={{ mr: 1 }}
                                  />
                                ) : (
                                  <Chip 
                                    icon={<Settings />}
                                    label={`${t('dashboard.mapping', 'Mapeo')}: ${item.mappingConfigs.length} ${t('dashboard.configurations', 'configuraciones')}`}
                                    size="small" 
                                    color="info" 
                                    variant="outlined"
                                    sx={{ mr: 1 }}
                                    title={item.mappingConfigs.map(c => c.name).join(', ')}
                                  />
                                )
                              ) : item.mappingConfig ? (
                                <Chip 
                                  icon={<Settings />}
                                  label={`${t('dashboard.mapping', 'Mapeo')}: ${item.mappingConfig.name}`}
                                  size="small" 
                                  color="info" 
                                  variant="outlined"
                                  sx={{ mr: 1 }}
                                />
                              ) : null}
                              <Chip 
                                icon={<Schedule />}
                                label={new Date(item.processedAt || '').toLocaleDateString(i18n.language === 'es' ? 'es-ES' : i18n.language, {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                                size="small" 
                                variant="outlined"
                              />
                            </Box>
                            </Typography>
                          }
                        />
                        <Box>
                          {loadingDetails && expandedRecord === item.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            isExpanded ? <ExpandLess /> : <ExpandMore />
                          )}
                        </Box>
                      </ListItem>
                      
                      {/* Detalles expandidos */}
                      {isExpanded && recordDetails && (
                        <Box sx={{ pl: 4, pr: 2, pb: 2 }}>
                          <Divider sx={{ mb: 2 }} />
                          <Typography variant="h6" gutterBottom>
                            {t('dashboard.processingDetails', 'Detalles del procesamiento')}
                          </Typography>
                          
                          {/* Información general */}
                          <Box component="div" sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              {t('common.summary', 'Resumen:')}
                            </Typography>
                            <Box component="div" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Chip 
                                label={`${t('common.total', 'Total')}: ${recordDetails.summary?.total ?? 0}`} 
                                color="primary" 
                                variant="outlined"
                              />
                              <Chip 
                                label={`${t('common.successful', 'Exitosos')}: ${recordDetails.summary?.success ?? 0}`} 
                                color="success" 
                                variant="outlined"
                              />
                              <Chip 
                                label={`${t('common.errors', 'Errores')}: ${recordDetails.summary?.errors ?? 0}`} 
                                color="error" 
                                variant="outlined"
                              />
                              {recordDetails.mappingConfigs && recordDetails.mappingConfigs.length > 0 ? (
                                recordDetails.mappingConfigs.length === 1 ? (
                                  <Chip 
                                    icon={<Settings />}
                                    label={`${t('dashboard.configuration', 'Configuración')}: ${recordDetails.mappingConfigs[0].name}`} 
                                    color="info" 
                                    variant="outlined"
                                  />
                                ) : (
                                  <Chip 
                                    icon={<Settings />}
                                    label={`${t('dashboard.configurations', 'Configuraciones')}: ${recordDetails.mappingConfigs.length}`} 
                                    color="info" 
                                    variant="outlined"
                                    title={recordDetails.mappingConfigs.map(c => c.name).join(', ')}
                                  />
                                )
                              ) : recordDetails.mappingConfig ? (
                                <Chip 
                                  icon={<Settings />}
                                  label={`${t('dashboard.configuration', 'Configuración')}: ${recordDetails.mappingConfig.name}`} 
                                  color="info" 
                                  variant="outlined"
                                />
                              ) : null}
                            </Box>
                          </Box>

                          {/* Configuraciones utilizadas */}
                          {recordDetails.mappingConfigs && recordDetails.mappingConfigs.length > 0 && (
                            <Box component="div" sx={{ mb: 3 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                {recordDetails.mappingConfigs.length === 1 
                                  ? t('dashboard.configuration', 'Configuración utilizada')
                                  : t('dashboard.configurations', 'Configuraciones utilizadas')} ({recordDetails.mappingConfigs.length})
                              </Typography>
                              <Stack spacing={1}>
                                {recordDetails.mappingConfigs.map((cfg, idx) => (
                                  <Paper key={cfg.id || idx} sx={{ p: 2, backgroundColor: 'grey.50' }}>
                                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                      <Settings color="primary" fontSize="small" />
                                      <Typography variant="body1" fontWeight="medium">
                                        {cfg.name}
                                      </Typography>
                                    </Box>
                                    {cfg.description && (
                                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        {cfg.description}
                                      </Typography>
                                    )}
                                    <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                                      {cfg.reportType && (
                                        <Chip 
                                          label={`${t('dashboard.reportType', 'Tipo')}: ${cfg.reportType}`}
                                          size="small"
                                          variant="outlined"
                                        />
                                      )}
                                      {cfg.reportTemplate && (
                                        <Chip 
                                          label={`${t('dashboard.template', 'Plantilla')}: ${cfg.reportTemplate}`}
                                          size="small"
                                          variant="outlined"
                                        />
                                      )}
                                      {cfg.fieldsCount !== undefined && cfg.fieldsCount > 0 && (
                                        <Chip 
                                          label={`${cfg.fieldsCount} ${t('dashboard.fields', 'campos')}`}
                                          size="small"
                                          variant="outlined"
                                        />
                                      )}
                                    </Box>
                                  </Paper>
                                ))}
                              </Stack>
                            </Box>
                          )}

                          {/* Filtros por campos normalizados */}
                          <Box component="div" sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              {t('dashboard.filters', 'Filtros por campos')}
                            </Typography>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} useFlexGap flexWrap="wrap">
                              {(['nif','grantorName','authorizationTo','responsibleFrom','email','contactEmail','phone','campaign'] as NormalizedFieldKey[]).map((key) => (
                                <FormControl key={key} sx={{ minWidth: 220 }} size="small">
                                  <InputLabel>{
                                    key === 'nif' ? t('common.nif','NIF') :
                                    key === 'grantorName' ? t('dashboard.grantorName','Grantor Name') :
                                    key === 'authorizationTo' ? t('dashboard.authTo','Auth to') :
                                    key === 'responsibleFrom' ? t('dashboard.responsable','Responsable') :
                                    key === 'email' ? t('common.email','Email') :
                                    key === 'contactEmail' ? t('dashboard.mainContactEmail','Main contact email') :
                                    key === 'phone' ? t('common.phone','Teléfono') :
                                    t('dashboard.campaign','Campaña')
                                  }</InputLabel>
                                  <Select
                                    multiple
                                    value={normalizedSelected[key]}
                                    label={
                                      key === 'nif' ? t('common.nif','NIF') :
                                      key === 'grantorName' ? t('dashboard.grantorName','Grantor Name') :
                                      key === 'authorizationTo' ? t('dashboard.authTo','Auth to') :
                                      key === 'responsibleFrom' ? t('dashboard.responsable','Responsable') :
                                      key === 'email' ? t('common.email','Email') :
                                      key === 'contactEmail' ? t('dashboard.mainContactEmail','Main contact email') :
                                      key === 'phone' ? t('common.phone','Teléfono') :
                                      t('dashboard.campaign','Campaña')
                                    }
                                    onChange={(e) => {
                                      const value = e.target.value as string[];
                                      setNormalizedSelected((prev) => ({ ...prev, [key]: value }));
                                    }}
                                    renderValue={(selected) => (selected as string[]).length > 0 ? (selected as string[]).join(', ') : ''}
                                  >
                                    {(normalizedOptions[key] || []).map((opt) => (
                                      <MenuItem key={opt} value={opt}>
                                        <Chip size="small" label={opt} sx={{ mr: 1 }} /> {opt}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              ))}
                            </Stack>
                          </Box>

                          {/* Lista de clientes procesados */}
                          {Array.isArray(recordDetails.results) && recordDetails.results.length > 0 && (
                            <Box component="div">
                              <Typography variant="subtitle2" gutterBottom>
                                {t('dashboard.processedClients', 'Clientes procesados:')}
                              </Typography>
                              <List dense>
                                {applyNormalizedFilters(recordDetails.results).map((client, clientIndex: number) => {
                                const nombre = (client.normalized?.grantorName) || client.clientData?.['Grantor Name'] || client.clientData?.Nombre || t('common.client', 'Cliente');
                                const email = client.normalized?.email || client.clientData?.Email || '';
                                const telefono = client.normalized?.phone || client.clientData?.['Teléfono'] || client.clientData?.Telefono || '';
                                const status = client.status || '';
                                const shareLink = client.shareLink || '';
                                const nif = client.nif || '';
                                // Solo mostrar timestamp si ya está guardado, no si está pendiente
                                const currentSentEmailAt = client.sentEmailAt || null;
                                // El checkbox debe estar marcado si ya está guardado o si hay un cambio pendiente
                                const isChecked = currentSentEmailAt !== null || pendingSentEmailUpdates[nif]?.checked === true;
                                const hasPendingUpdate = pendingSentEmailUpdates[nif] !== undefined;
                                const isUpdating = updatingSentEmail[nif] || false;
                                  return (
                                  <ListItem key={clientIndex} sx={{ pl: 0 }}>
                                    <ListItemIcon>
                                      <Person color="primary" />
                                    </ListItemIcon>
                                    <Box sx={{ flex: 1 }}>
                                      <Box sx={{ mb: 1 }}>
                                        <Typography variant="body1" fontWeight="medium">
                                          {nombre}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                          <Chip 
                                            icon={<Person />}
                                            label={`${t('common.nif', 'NIF')}: ${client.nif || ''}`} 
                                            size="small" 
                                            variant="outlined"
                                          />
                                          {email && (
                                            <Chip 
                                              icon={<Email />}
                                              label={email} 
                                              size="small" 
                                              variant="outlined"
                                            />
                                          )}
                                          {telefono && (
                                            <Chip 
                                              icon={<Phone />}
                                              label={telefono} 
                                              size="small" 
                                              variant="outlined"
                                            />
                                          )}
                                        </Box>
                                      </Box>
                                      <Box sx={{ mt: 1 }}>
                                        <Typography 
                                          variant="body2" 
                                          color={status === 'error' ? 'error' : 'textSecondary'}
                                        >
                                          {String(client.message || '')}
                                        </Typography>
                        {status === 'error' && (
                          <Chip 
                            label={t('dashboard.duplicateClient', 'Error: Cliente duplicado')} 
                            size="small" 
                            color="error" 
                            sx={{ mt: 1 }}
                          />
                        )}
                                        {!!shareLink && status !== 'error' && (
                                          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            <Chip 
                                              icon={<Link />}
                                              label={t('dashboard.viewShareLink', 'Ver enlace de compartir')} 
                                              size="small" 
                                              color="primary"
                                              clickable
                                              onClick={() => window.open(shareLink, '_blank')}
                                            />
                                            <Chip
                                              icon={<Description />}
                                              label={t('dashboard.documents', 'Documentos')}
                                              size="small"
                                              color="primary"
                                              clickable
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                openDocumentsModal(expandedRecord as number, client.nif || '');
                                              }}
                                            />
                                          </Box>
                                        )}
                                        {/* Campo Sent Email */}
                                        {status !== 'error' && nif && (
                                          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                            <FormControlLabel
                                              control={
                                                <Checkbox
                                                  checked={isChecked}
                                                  onChange={(e) => handleSentEmailCheckboxChange(nif, e.target.checked)}
                                                  disabled={isUpdating}
                                                  icon={<Send />}
                                                  checkedIcon={<CheckCircle />}
                                                />
                                              }
                                              label={currentSentEmailAt ? t('dashboard.sentEmail', 'Sent Email') : t('dashboard.sendEmail', 'Send Email')}
                                            />
                                            {isChecked && currentSentEmailAt && (
                                              <Typography variant="caption" color="text.secondary">
                                                {new Date(currentSentEmailAt).toLocaleString(i18n.language === 'es' ? 'es-ES' : i18n.language, {
                                                  year: 'numeric',
                                                  month: '2-digit',
                                                  day: '2-digit',
                                                  hour: '2-digit',
                                                  minute: '2-digit',
                                                  second: '2-digit',
                                                })}
                                              </Typography>
                                            )}
                                            {hasPendingUpdate && (
                                              <Button
                                                size="small"
                                                variant="contained"
                                                color="primary"
                                                onClick={() => handleApplySentEmail(nif)}
                                                disabled={isUpdating}
                                                startIcon={isUpdating ? <CircularProgress size={16} /> : <CheckCircle />}
                                              >
                                                {t('common.apply', 'Apply')}
                                              </Button>
                                            )}
                                          </Box>
                                        )}
                                      </Box>
                                    </Box>
                                  </ListItem>
                                  );
                                })}
                              </List>
                            </Box>
                          )}
                        </Box>
                      )}
                      
                      {index < (data?.records?.length || 0) - 1 && <Divider />}
                    </React.Fragment>
                  );
                })}
              </List>
            )
          ) : (
            <Paper 
              elevation={0} 
              sx={{ 
                p: 4, 
                textAlign: 'center', 
                backgroundColor: 'grey.50',
                borderRadius: 2 
              }}
            >
              <CloudUpload sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                {t('dashboard.noData', 'No hay datos procesados disponibles')}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t('dashboard.uploadFirst', '¡Sube tu primer archivo para comenzar!')}
              </Typography>
            </Paper>
          )}
        </CardContent>
        
        <CardActions sx={{ justifyContent: 'center', p: 2 }}>
          <Button
            variant="contained"
            startIcon={<Upload />}
            onClick={() => navigate('/upload')}
            size="large"
          >
            {t('dashboard.uploadFile', 'Subir Archivo')}
          </Button>
        </CardActions>
      </Card>

      {/* Detalles expandidos para vista de tabla de administradores */}
      {user?.role === 'admin' && expandedRecord && recordDetails && (
        <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            {t('dashboard.processingDetails', 'Detalles del procesamiento')}
          </Typography>
          
          {/* Información general */}
          <Box component="div" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('common.summary', 'Resumen:')}
            </Typography>
            <Box component="div" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip 
                label={`${t('common.total', 'Total')}: ${recordDetails.summary?.total ?? 0}`} 
                color="primary" 
                variant="outlined"
              />
              <Chip 
                label={`${t('common.successful', 'Exitosos')}: ${recordDetails.summary?.success ?? 0}`} 
                color="success" 
                variant="outlined"
              />
              <Chip 
                label={`${t('common.errors', 'Errores')}: ${recordDetails.summary?.errors ?? 0}`} 
                color="error" 
                variant="outlined"
              />
              {recordDetails.mappingConfig && (
                <Chip 
                  icon={<Settings />}
                  label={`${t('dashboard.configuration', 'Configuración')}: ${recordDetails.mappingConfig.name}`} 
                  color="info" 
                  variant="outlined"
                />
              )}
            </Box>
          </Box>

          {/* Filtros por campos normalizados */}
          <Box component="div" sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle2">
              {t('dashboard.filters', 'Filtros por campos')}
            </Typography>
              {Array.isArray(recordDetails.results) && recordDetails.results.length > 0 && (
                <Chip
                  label={`${applyNormalizedFilters(recordDetails.results).length} ${t('common.of', 'de')} ${recordDetails.results.length}`}
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 'bold' }}
                />
              )}
            </Box>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} useFlexGap flexWrap="wrap">
              {(['nif','grantorName','authorizationTo','responsibleFrom','email','contactEmail','campaign'] as NormalizedFieldKey[]).map((key) => (
                <FormControl key={key} sx={{ minWidth: 220 }} size="small">
                  <InputLabel>{
                    key === 'nif' ? t('common.nif','NIF') :
                    key === 'grantorName' ? t('dashboard.grantorName','Grantor Name') :
                    key === 'authorizationTo' ? t('dashboard.authTo','Auth to') :
                    key === 'responsibleFrom' ? t('dashboard.responsable','Responsable') :
                    key === 'email' ? t('common.email','Email') :
                    key === 'contactEmail' ? t('dashboard.mainContactEmail','Main contact email') :
                    key === 'phone' ? t('common.phone','Teléfono') :
                    t('dashboard.campaign','Campaña')
                  }</InputLabel>
                  <Select
                    multiple
                    value={normalizedSelected[key]}
                    label={
                      key === 'nif' ? t('common.nif','NIF') :
                      key === 'grantorName' ? t('dashboard.grantorName','Grantor Name') :
                      key === 'authorizationTo' ? t('dashboard.authTo','Auth to') :
                      key === 'responsibleFrom' ? t('dashboard.responsable','Responsable') :
                      key === 'email' ? t('common.email','Email') :
                      key === 'contactEmail' ? t('dashboard.mainContactEmail','Main contact email') :
                      key === 'phone' ? t('common.phone','Teléfono') :
                      t('dashboard.campaign','Campaña')
                    }
                    onChange={(e) => {
                      const value = e.target.value as string[];
                      setNormalizedSelected((prev) => ({ ...prev, [key]: value }));
                    }}
                    renderValue={(selected) => (selected as string[]).length > 0 ? (selected as string[]).join(', ') : ''}
                  >
                    {(normalizedOptions[key] || []).map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        <Chip size="small" label={opt} sx={{ mr: 1 }} /> {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ))}
            </Stack>
          </Box>

          {/* Lista de clientes procesados */}
          {Array.isArray(recordDetails.results) && recordDetails.results.length > 0 && (
            <Box component="div">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="subtitle2">
              {t('dashboard.processedClients', 'Clientes procesados:')}
            </Typography>
              <Chip
                label={`${applyNormalizedFilters(recordDetails.results).length} ${t('common.of', 'de')} ${recordDetails.results.length}`}
                color="primary"
                size="small"
                sx={{ fontWeight: 'bold' }}
              />
            </Box>
              <List dense>
                {applyNormalizedFilters(recordDetails.results).map((client, clientIndex: number) => {
                const nombre = (client.normalized?.grantorName) || client.clientData?.['Grantor Name'] || client.clientData?.Nombre || t('common.client', 'Cliente');
                  const email = client.normalized?.email || client.clientData?.Email || '';
                  const telefono = client.normalized?.phone || client.clientData?.['Teléfono'] || client.clientData?.Telefono || '';
                  const status = client.status || '';
                  const shareLink = client.shareLink || '';
                  const nif = client.nif || '';
                  const authTo = (client.normalized?.authorizationTo) || client.clientData?.['Authorization to'] || '';
                  const campaign = (client.normalized?.campaign) || client.clientData?.['Campa¤a Octubre 2025'] || '';
                  const responsible = (client.normalized as any)?.responsibleFrom || client.clientData?.['Responsable'] || client.clientData?.['Responsable AEAT'] || '';
                  const mainContactEmail = (client.normalized as any)?.contactEmail || client.clientData?.['Main contact email'] || client.clientData?.['Contact Email'] || client.clientData?.['Contact email'] || '';
                  // Solo mostrar timestamp si ya está guardado, no si está pendiente
                  const currentSentEmailAt = client.sentEmailAt || null;
                  // El checkbox debe estar marcado si ya está guardado o si hay un cambio pendiente
                  const isChecked = currentSentEmailAt !== null || pendingSentEmailUpdates[nif]?.checked === true;
                  const hasPendingUpdate = pendingSentEmailUpdates[nif] !== undefined;
                  const isUpdating = updatingSentEmail[nif] || false;
                  return (
                  <ListItem 
                    key={clientIndex} 
                    sx={{ 
                      pl: 0,
                      mb: 2,
                      '&:not(:last-child)': {
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        pb: 2,
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        p: 2,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: clientIndex % 2 === 0 ? 'background.paper' : 'action.hover',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          boxShadow: 1,
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Box sx={{ 
                          minWidth: 40,
                          height: 40,
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold',
                          fontSize: '0.875rem'
                        }}>
                          {clientIndex + 1}
                        </Box>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body1" fontWeight="medium">
                          {nombre}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                          <Chip 
                            icon={<Person />}
                            label={`${t('common.nif', 'NIF')}: ${client.nif || ''}`} 
                            size="small" 
                            variant="outlined"
                          />
                          {email && (
                            <Chip 
                              icon={<Email />}
                              label={email} 
                              size="small" 
                              variant="outlined"
                            />
                          )}
                          {telefono && (
                            <Chip 
                              icon={<Phone />}
                              label={telefono} 
                              size="small" 
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ mt: 1 }}>
                        <Typography 
                          variant="body2" 
                          color={status === 'error' ? 'error' : 'textSecondary'}
                        >
                          {String(client.message || '')}
                        </Typography>
                                      {status === 'error' && (
                                        <Chip 
                                          label={t('dashboard.duplicateClient', 'Error: Cliente duplicado')} 
                                          size="small" 
                                          color="error" 
                                          sx={{ mt: 1 }}
                                        />
                                      )}
                                      {(authTo || campaign || responsible || mainContactEmail) && (
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                                          {!!authTo && (
                                            <Chip 
                                              label={`${t('dashboard.authTo', 'Auth to')}: ${authTo}`} 
                                              size="small" 
                                            />
                                          )}
                                          {!!campaign && (
                                            <Chip 
                                              label={`${t('dashboard.campaign', 'Campaña')}: ${campaign}`} 
                                              size="small" 
                                            />
                                          )}
                                          {!!responsible && (
                                            <Chip 
                                              label={`${t('dashboard.responsable', 'Responsable')}: ${responsible}`} 
                                              size="small" 
                                            />
                                          )}
                                          {!!mainContactEmail && (
                                            <Chip 
                                              label={`${t('dashboard.mainContactEmail', 'Main contact email')}: ${mainContactEmail}`} 
                                              size="small" 
                                            />
                                          )}
                                        </Box>
                                      )}
                        {!!shareLink && status !== 'error' && (
                          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip 
                              icon={<Link />}
                              label={t('dashboard.viewShareLink', 'Ver enlace de compartir')} 
                              size="small" 
                              color="primary"
                              clickable
                              onClick={() => window.open(shareLink, '_blank')}
                            />
                            <Chip
                              icon={<Description />}
                              label={t('dashboard.documents', 'Documentos')}
                              size="small"
                              color="primary"
                              clickable
                              onClick={() => openDocumentsModal(expandedRecord as number, client.nif || '')}
                            />
                          </Box>
                        )}
                        {/* Campo Sent Email */}
                        {status !== 'error' && nif && (
                          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={isChecked}
                                  onChange={(e) => handleSentEmailCheckboxChange(nif, e.target.checked)}
                                  disabled={isUpdating}
                                  icon={<Send />}
                                  checkedIcon={<CheckCircle />}
                                />
                              }
                              label={currentSentEmailAt ? t('dashboard.sentEmail', 'Sent Email') : t('dashboard.sendEmail', 'Send Email')}
                            />
                            {isChecked && currentSentEmailAt && (
                              <Typography variant="caption" color="text.secondary">
                                {new Date(currentSentEmailAt).toLocaleString(i18n.language === 'es' ? 'es-ES' : i18n.language, {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  second: '2-digit',
                                })}
                              </Typography>
                            )}
                            {hasPendingUpdate && (
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={() => handleApplySentEmail(nif)}
                                disabled={isUpdating}
                                startIcon={isUpdating ? <CircularProgress size={16} /> : <CheckCircle />}
                              >
                                {t('common.apply', 'Apply')}
                              </Button>
                            )}
                          </Box>
                        )}
                      </Box>
                        </Box>
                      </Box>
                    </Box>
                  </ListItem>
                  );
                })}
              </List>
            </Box>
          )}
        </Box>
      )}

      {/* Botón flotante para subir archivos */}
      <Fab
        color="primary"
        aria-label="subir archivo"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
        }}
        onClick={() => navigate('/upload')}
      >
        <Upload />
      </Fab>

      {/* Modal de documentos generados */}
      {docsOpen && (
        <Box>
          <Dialog 
            open={docsOpen} 
            onClose={closeDocumentsModal} 
            maxWidth={false}
            PaperProps={{ sx: { width: '85vw', maxWidth: '85vw' } }}
          >
            <Box sx={{ p: 2 }}>
              <Typography variant="h6">{t('dashboard.generatedDocuments', 'Documentos generados')} {docsClientNif ? `(${t('common.nif', 'NIF')}: ${docsClientNif})` : ''}</Typography>
            </Box>
            <Divider />
            <Box sx={{ p: 2 }}>
              {docsLoading ? (
                <Box display="flex" alignItems="center" justifyContent="center" minHeight={160}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('common.nif', 'NIF')}</TableCell>
                        <TableCell>{t('dashboard.type')}</TableCell>
                        <TableCell>DOCX</TableCell>
                        <TableCell>PDF</TableCell>
                        <TableCell>{t('common.actions', 'Acciones')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {docsList.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5}>
                            <Typography variant="body2" color="textSecondary">{t('dashboard.noDocuments', 'No hay documentos generados.')}</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {docsList.map((d) => (
                        <TableRow key={d.id} hover>
                          <TableCell>{d.nif || ''}</TableCell>
                          <TableCell>
                            <Chip 
                              label={d.tipo === 'Uploaded' ? t('dashboard.uploaded') : t('dashboard.generated')}
                              color={d.tipo === 'Uploaded' ? 'primary' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{d.nameDocumentDocx || '-'}</TableCell>
                          <TableCell>{d.nameDocumentPdf || d.originalFileName || '-'}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1}>
                              {d.tipo === 'Generated' && d.nameDocumentPdf && (
                                <Button size="small" variant="outlined" onClick={() => previewPdf(d.id, d.nameDocumentPdf || undefined)}>{t('common.view', 'Ver')}</Button>
                              )}
                              {d.tipo === 'Generated' && d.nameDocumentPdf && (
                                <Button size="small" onClick={() => downloadDocument(d.id, 'pdf', d.nameDocumentPdf || undefined)}>{t('dashboard.downloadPdf', 'Descargar PDF')}</Button>
                              )}
                              {d.tipo === 'Generated' && d.nameDocumentDocx && (
                                <Button size="small" onClick={() => downloadDocument(d.id, 'docx', d.nameDocumentDocx || undefined)}>{t('dashboard.downloadDocx', 'Descargar DOCX')}</Button>
                              )}
                              {d.tipo === 'Uploaded' && (
                                <>
                                  <Button 
                                    size="small" 
                                    variant="outlined" 
                                    onClick={() => previewUploadedFile(d.id, d.originalFileName || d.nameDocumentPdf || undefined)}
                                  >
                                    {t('common.view', 'Ver')}
                                  </Button>
                                  <Button 
                                    size="small" 
                                    onClick={() => downloadUploadedFile(d.id, d.originalFileName || d.nameDocumentPdf || undefined)}
                                  >
                                    {t('dashboard.download')}
                                  </Button>
                                </>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
            {!!previewUrl && (
              <Box sx={{ px: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('dashboard.preview', 'Previsualización')}: {previewName}</Typography>
                <Box sx={{ height: 500, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                  <iframe title="preview-pdf" src={previewUrl} style={{ width: '100%', height: '100%', border: 'none' }} />
                </Box>
              </Box>
            )}
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={closeDocumentsModal}>{t('common.close', 'Cerrar')}</Button>
            </Box>
          </Dialog>
        </Box>
      )}
    </Box>
  );
};

export default DashboardPage;